import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Lock, Save } from 'lucide-react';
import type { AppPermissionScope, User } from '@bliks-io/contracts';
import { Alert, AlertDescription, Button, Label, cn } from '@bliks-io/ui';

import { useUpdateAppSeats } from '@/api/queries/useInstalledApps';
import { FormFieldUserSelect } from '@/components/form/FormFieldUserSelect';
import { formatSeatLimit } from '@/lib/seat-limits';
import type { MergedAppRow } from '@/lib/merge-app-catalog';

interface AppSeatsPanelProps {
  app: MergedAppRow;
}

/**
 * Org managers reach every app implicitly and consume no seat, so offering them
 * as NEW assignees would only burn seats without granting anything; a suspended
 * account cannot sign in, so seating one would burn a seat the same way. A user
 * who already holds a seat is a different case: their id still occupies it, so
 * they must stay visible to be deselected — see `isSelectable`.
 */
function isSeatEligible(user: User) {
  return !user.userTypes.includes('org_manager') && user.status !== 'suspended';
}

function hasSameMembers(a: string[], b: string[]) {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Keys are colon-free (`:` is i18next's namespace separator — a raw scope as
 * key silently resolves to nothing) and each label states the EFFECTIVE grant
 * (cost figures ride on `read:process-steps`). A Map, because scopes are
 * free-form strings and an object index would resolve `constructor`.
 */
const seatDataCategoryKeyByScope = new Map<string, string>(
  Object.entries({
    'read:processes': 'common.Apps.seatDataCategories.processes',
    'read:process-steps': 'common.Apps.seatDataCategories.processSteps',
    'read:surveys': 'common.Apps.seatDataCategories.surveys',
    'read:systems': 'common.Apps.seatDataCategories.systems',
    'read:roles': 'common.Apps.seatDataCategories.roles',
    'read:teams': 'common.Apps.seatDataCategories.teams',
    'read:departments': 'common.Apps.seatDataCategories.departments',
    'read:org-members': 'common.Apps.seatDataCategories.orgMembers',
    'read:artifacts': 'common.Apps.seatDataCategories.artifacts',
    'read:external-contacts': 'common.Apps.seatDataCategories.externalContacts',
    'read:process-chains': 'common.Apps.seatDataCategories.processChains'
  } satisfies Record<AppPermissionScope, string>)
);

/**
 * The seat states a system admin has to act on. Zero assigned seats is NOT one
 * of them: an enabled app with zero assignees is still reachable by org managers,
 * who reach every app implicitly and consume no seat.
 */
function SeatAlert({ over, limit }: { over: number; limit: number | null }) {
  const { t } = useTranslation();

  if (over > 0) {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {t('common.Apps.seatsOverLimit', { over })}
      </span>
    );
  }
  if (limit === 0) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Lock className="h-4 w-4 shrink-0" />
        {t('common.Apps.seatsNoCapacity')}
      </span>
    );
  }
  return null;
}

export function AppSeatsPanel({ app }: AppSeatsPanelProps) {
  const { t } = useTranslation();
  const updateSeats = useUpdateAppSeats();
  const [draft, setDraft] = useState<string[] | null>(null);

  const assigned = app.assignedUserIds ?? [];
  const selected = draft ?? assigned;
  const limit = app.seatLimit ?? null;
  const over = limit === null ? 0 : selected.length - limit;
  const hasChanges = draft !== null && !hasSameMembers(draft, assigned);

  const isSelectable = (user: User) => isSeatEligible(user) || selected.includes(user._id);

  const dataCategories = (app.permissions ?? []).map((scope) => {
    const key = seatDataCategoryKeyByScope.get(scope);
    return key ? t(key) : scope;
  });

  const handleSave = () => {
    updateSeats.mutate(
      { appId: app.appId, userIds: selected },
      { onSuccess: () => setDraft(null) }
    );
  };

  const seatsFieldId = `app-seats-${app.appId}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor={seatsFieldId}>{t('common.Apps.seatsMembers')}</Label>
        <span
          className={cn(
            'text-sm tabular-nums',
            over > 0 ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {t('common.Apps.seatsCount', {
            used: selected.length,
            limit: formatSeatLimit(limit)
          })}
        </span>
      </div>

      {dataCategories.length > 0 && (
        <Alert colorScheme="info">
          <Info className="w-4 h-4" />
          <AlertDescription>
            {t('common.Apps.seatDataIntro', { categories: dataCategories.join(', ') })}
          </AlertDescription>
        </Alert>
      )}

      <FormFieldUserSelect
        id={seatsFieldId}
        multiple
        value={selected}
        onChange={(value) => setDraft(Array.isArray(value) ? value : [])}
        // Every non-deleted user, overriding the picker defaults (assignable
        // statuses, internal only): a suspended or external assignee keeps
        // occupying the cap, so hiding them would leave that seat
        // unreleasable from the UI.
        queryOptions={{ status: undefined, classification: undefined }}
        filter={isSelectable}
      />

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <SeatAlert over={over} limit={limit} />
        </div>
        <Button
          onClick={handleSave}
          loading={updateSeats.isPending}
          disabled={!hasChanges || over > 0}
        >
          <Save className="w-4 h-4 mr-2" />
          {t('common.Save')}
        </Button>
      </div>
    </div>
  );
}
